import { useDistortionGrid } from "./DistortionGridContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { Download, LayoutGrid, Shapes, Share2, Upload, Waves } from "lucide-react";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import {
    NOISE_DISPLACEMENT_STYLE_OPTIONS,
    type NoiseDisplacementStyle,
} from "../../utils/displacementMap";

export function DistortionGridControls() {
    const {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        viewBoxAttr,
        cols,
        rows,
        maxStrength,
        algorithm,
        seed,
        noiseScale,
        octaves,
        circleSegments,
        setCols,
        setRows,
        setMaxStrength,
        setAlgorithm,
        setSeed,
        setNoiseScale,
        setOctaves,
        setCircleSegments,
        exportSvgString,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useDistortionGrid();

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    return (
        <div className="panel-controls distortion-grid-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Distortion Grid</h2>
                    <ToolDescriptionPopover dialogLabel="Distortion Grid tool description">
                        <p>
                            Import an SVG and generate a grid where each cell
                            shows the artwork with progressively increasing path
                            distortion, left-to-right, top-to-bottom.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="distortion-grid"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="SVG source" icon={Upload}>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file…
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleSvgLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info upload-info--tight-top">
                            <strong>{svg.name}</strong>
                            {viewBoxAttr && (
                                <>
                                    {" "}
                                    — viewBox: <code>{viewBoxAttr}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!svg && (
                        <p className="upload-hint">
                            Paths, polygons, circles, and rects are
                            distorted.
                        </p>
                    )}
                    {error && <p className="upload-error">{error}</p>}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Grid" icon={LayoutGrid}>
                    <div className="field-row">
                        <label className="field-label">
                            Columns
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={16}
                                step={1}
                                value={cols}
                                onChange={handleInt(setCols, 1)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={16}
                                step={1}
                                value={rows}
                                onChange={handleInt(setRows, 1)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Distortion" icon={Waves}>
                    <div className="field-row">
                        <label className="field-label">
                            Path warp style
                            <select
                                value={algorithm}
                                onChange={(e) =>
                                    setAlgorithm(
                                        e.target
                                            .value as NoiseDisplacementStyle,
                                    )
                                }
                            >
                                {NOISE_DISPLACEMENT_STYLE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max strength (SVG units)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={10000}
                                step={1}
                                value={maxStrength}
                                onChange={handleFloat(setMaxStrength, 0, 10000)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Noise scale
                            <DebouncedInput
                                type="number"
                                min={0.0001}
                                max={0.5}
                                step={0.0005}
                                value={noiseScale}
                                onChange={handleFloat(
                                    setNoiseScale,
                                    0.0001,
                                    0.5,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            FBM octaves
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={8}
                                step={1}
                                value={octaves}
                                onChange={handleInt(setOctaves, 1)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Shape options" icon={Shapes}>
                    <div className="field-row">
                        <label className="field-label">
                            Circle / ellipse segments
                            <DebouncedInput
                                type="number"
                                min={6}
                                max={256}
                                step={1}
                                value={circleSegments}
                                onChange={handleInt(setCircleSegments, 6)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Export" icon={Download}>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport || !exportSvgString}
                        getSvg={() =>
                            exportSvgString
                                ? {
                                      name: "distortion-grid-export.svg",
                                      content: exportSvgString,
                                  }
                                : null
                        }
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["distortion-grid"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
